import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo, useState} from "react";
import {trailersSelector, useGetAllTrailersQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {TrailerDataTable} from "../../components/data-table/TrailerDataTable/TrailerDataTable";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {useLocation, useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

export type TrailerPageProps = {
  showStatus?: boolean
}

export const TrailerDataTablePageRoute = () => {
  const {state} = useLocation();
  const showStatus = state?.showStatus;
  return (
    <TrailerDataTablePage
      showStatus={showStatus}
    />
  );
};

export const TrailerDataTablePage = (props: TrailerPageProps) => {
  const {showStatus} = props;

  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp();
  const navigate = useNavigate()

  const [access, setAccess] = useState(false);

  const selectTrailers = useMemo(() => {
    return trailersSelector();
  }, []);

  const {trailers, isLoading, error, refetch,} = useGetAllTrailersQuery(access ? (regionId ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      trailers: selectTrailers(result, undefined),
      isLoading: result.isLoading,
      error: result.error,
    })
  });

  return (
    <ProtectedPage permissions={["trailer_view"]} isLoading={isLoading} error={error} onAccess={setAccess}>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title order={4}>
                      {t("common.entity.trailer.plural", "Trailers", {ns: "common"})}
                    </Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.actions.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["trailer_create"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/trailers/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <TrailerDataTable
                  withBorder
                  records={trailers}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isLoading}
                  showStatus={showStatus}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  );
};
